/**
 * デイサービス利用中止日に基づく家族アプリのやりとり送信可否判定。
 *
 * 仕様:
 *  - `service_end_date` が空 / 未設定 → 送信可能（false）
 *  - JST 基準で「今日」が `service_end_date` 翌日以降 → 送信閉鎖（true）
 *  - 端末ローカル TZ には依存せず、UTC エポックに +9h を足してから
 *    UTC 部品（getUTCFullYear 等）で JST のカレンダー日を取り出す
 */

const JST_OFFSET_MS = 9 * 60 * 60 * 1000;

const toJstYmd = (date: Date): { y: number; m: number; d: number } | null => {
  const t = date.getTime();
  if (Number.isNaN(t)) return null;
  const jst = new Date(t + JST_OFFSET_MS);
  return {
    y: jst.getUTCFullYear(),
    m: jst.getUTCMonth() + 1,
    d: jst.getUTCDate(),
  };
};

const compareYmd = (
  a: { y: number; m: number; d: number },
  b: { y: number; m: number; d: number },
): number => {
  if (a.y !== b.y) return a.y - b.y;
  if (a.m !== b.m) return a.m - b.m;
  return a.d - b.d;
};

interface PatientLike {
  service_end_date?: string | null;
}

/** YYYY-MM-DD 文字列を実在するカレンダー日として検証してから {y,m,d} に分解する。 */
const parseYmd = (raw: string): { y: number; m: number; d: number } | null => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(raw);
  if (!match) return null;

  const y = Number(match[1]);
  const m = Number(match[2]);
  const d = Number(match[3]);

  // 2026-02-31 のような正規表現は通るが実在しない日付を弾く。
  // Date.UTC は範囲外の月日を繰り上げ・繰り下げするため、構築後の値が
  // 入力と一致しなければ無効と判断する。
  const utc = new Date(Date.UTC(y, m - 1, d));
  if (
    Number.isNaN(utc.getTime()) ||
    utc.getUTCFullYear() !== y ||
    utc.getUTCMonth() !== m - 1 ||
    utc.getUTCDate() !== d
  ) {
    return null;
  }

  return { y, m, d };
};

export const isMessageSendingClosed = (
  patient: PatientLike | null | undefined,
  now: Date = new Date(),
): boolean => {
  const raw = patient?.service_end_date;
  if (!raw) return false;

  const endYmd = parseYmd(raw);
  if (!endYmd) return false; // fail-open: 不正な日付値は送信可能扱い

  const todayJst = toJstYmd(now);
  if (!todayJst) return false;

  return compareYmd(todayJst, endYmd) > 0;
};
